import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

const Header = () => {
  const location = useLocation();
  const pathname = location.pathname;
  
  // Check if we're on Leipzig-related pages
  const isLeipzig = pathname.includes('/leipzig') || 
    (pathname === '/angebot' && sessionStorage.getItem('lastLocation') === 'leipzig') ||
    (pathname === '/kontakt' && sessionStorage.getItem('lastLocation') === 'leipzig') ||
    (pathname === '/leistungen' && sessionStorage.getItem('lastLocation') === 'leipzig') ||
    (pathname.includes('/leistungen/') && sessionStorage.getItem('lastLocation') === 'leipzig');
  
  // Check if we're on Halle-related pages
  const isHalle = pathname.includes('/halle') || 
    (pathname === '/angebot' && sessionStorage.getItem('lastLocation') === 'halle') ||
    (pathname === '/kontakt' && sessionStorage.getItem('lastLocation') === 'halle') ||
    (pathname === '/leistungen' && sessionStorage.getItem('lastLocation') === 'halle') ||
    (pathname.includes('/leistungen/') && sessionStorage.getItem('lastLocation') === 'halle');

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Store location when visiting Leipzig or Halle pages
  if (pathname.includes('/leipzig')) {
    sessionStorage.setItem('lastLocation', 'leipzig');
  } else if (pathname.includes('/halle')) {
    sessionStorage.setItem('lastLocation', 'halle');
  } else if (pathname === '/') {
    sessionStorage.removeItem('lastLocation');
  }

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  // Get navigation items based on current location
  const getNavItems = () => {
    if (isLeipzig) {
      return [
        { to: '/', label: 'Startseite' },
        { to: '/leipzig', label: 'Leipzig' },
        { to: '/leipzig/ueber-uns', label: 'Über uns' },
        { to: '/leistungen', label: 'Leistungen' },
        { to: '/leipzig/freie-kapazitaeten', label: 'Freie Kapazitäten' },
        { to: '/leipzig/stellenausschreibung', label: 'Karriere' },
        { to: '/angebot', label: 'Angebot' },
      ];
    }
    if (isHalle) {
      return [
        { to: '/', label: 'Startseite' },
        { to: '/halle', label: 'Halle (Saale)' },
        { to: '/halle/ueber-uns', label: 'Über uns' },
        { to: '/leistungen', label: 'Leistungen' },
        { to: '/halle/freie-kapazitaeten', label: 'Freie Kapazitäten' },
        { to: '/halle/karriere', label: 'Karriere' },
        { to: '/angebot', label: 'Angebot' },
      ];
    }
    return [
      { to: '/', label: 'Startseite' },
      { to: '/leistungen', label: 'Leistungen' },
      { to: '/', label: 'Standorte', hash: '#locations' },
      { to: '/kontakt', label: 'Kontakt' },
    ];
  };

  const navItems = getNavItems();

  return (
    <header className="bg-white shadow-sm sticky top-0 z-50" data-testid="main-header">
      <div className="container mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center space-x-4" onClick={closeMobileMenu}>
            <img 
              src="https://beratung.grossstadtherzen.de/logo-transparent.png" 
              alt="Großstadtherzen Logo" 
              className="h-20 md:h-32 w-auto"
              data-testid="header-logo"
            />
          </Link>
          
          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-6" data-testid="main-navigation">
            {navItems.map((item, index) => (
              <Link 
                key={index}
                to={item.hash ? `${item.to}${item.hash}` : item.to} 
                className={`transition-colors font-medium ${
                  pathname === item.to 
                    ? 'text-turquoise-400' 
                    : 'text-gray-700 hover:text-turquoise-400'
                }`}
              >
                {item.label}
              </Link>
            ))}
          </nav>

          {/* Mobile menu button */}
          <button 
            className="md:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors" 
            data-testid="mobile-menu-button"
            onClick={toggleMobileMenu}
            aria-label="Menü öffnen"
          >
            {mobileMenuOpen ? (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            )}
          </button>
        </div>

        {/* Mobile Navigation Dropdown */}
        {mobileMenuOpen && (
          <nav 
            className="md:hidden mt-4 pb-4 border-t border-gray-100 pt-4"
            data-testid="mobile-navigation"
          >
            <div className="flex flex-col space-y-3">
              {navItems.map((item, index) => (
                <Link 
                  key={index}
                  to={item.hash ? `${item.to}${item.hash}` : item.to} 
                  className={`transition-colors font-medium py-2 px-4 rounded-lg ${
                    pathname === item.to 
                      ? 'text-turquoise-400 bg-turquoise-50' 
                      : 'text-gray-700 hover:text-turquoise-400 hover:bg-turquoise-50'
                  }`}
                  onClick={closeMobileMenu}
                >
                  {item.label}
                </Link>
              ))}
              
              {/* Location Quick Links in Mobile - only show when not in a location context */}
              {!isLeipzig && !isHalle && (
                <div className="border-t border-gray-100 pt-3 mt-3">
                  <p className="text-sm text-gray-500 px-4 mb-2">Standorte</p>
                  <Link 
                    to="/leipzig" 
                    className="text-gray-700 hover:text-turquoise-400 transition-colors font-medium py-2 px-4 rounded-lg hover:bg-turquoise-50 flex items-center"
                    onClick={closeMobileMenu}
                  >
                    <span className="bg-turquoise-400 text-white w-6 h-6 rounded-full flex items-center justify-center mr-3 text-xs">L</span>
                    Leipzig
                  </Link>
                  <Link 
                    to="/halle" 
                    className="text-gray-700 hover:text-turquoise-400 transition-colors font-medium py-2 px-4 rounded-lg hover:bg-turquoise-50 flex items-center"
                    onClick={closeMobileMenu}
                  >
                    <span className="bg-turquoise-400 text-white w-6 h-6 rounded-full flex items-center justify-center mr-3 text-xs">H</span>
                    Halle (Saale)
                  </Link>
                </div>
              )}

              {/* Contact Info in Mobile */}
              <div className="border-t border-gray-100 pt-3 mt-3">
                <p className="text-sm text-gray-500 px-4 mb-2">Kontakt</p>
                <a 
                  href="tel:034124927833" 
                  className="text-turquoise-400 font-medium py-2 px-4 flex items-center"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  0341-24927833
                </a>
              </div>
            </div>
          </nav>
        )}
      </div>
    </header>
  );
};

export default Header;
